import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import UserControls from 'flarum/forum/utils/UserControls';
import CommentPost from 'flarum/forum/components/CommentPost';
import SettingsPage from 'flarum/forum/components/SettingsPage';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import FieldSet from 'flarum/common/components/FieldSet';
import Separator from 'flarum/common/components/Separator';

// '나만의 비밀방' 태그 ID
const PRIVATE_TAG_ID = '3';
// '함께 읽는 방' 태그 ID
const LOUNGE_TAG_ID = '2';
// '공유됨' 보조 태그 ID (재공유 방지 마커용)
const SHARED_TAG_ID = '4';

// 카카오 가입자의 합성 이메일 도메인 (src/OAuth/Kakao.php 와 반드시 일치)
const SYNTHETIC_EMAIL_DOMAIN = '@users.onu.today';

/* ============================================================================
 *  2026-08-02 · 모바일 우선 개편
 * ----------------------------------------------------------------------------
 *  Flarum 은 767px 이하에서 글 목록의 '⋮' 컨트롤을 기본으로 숨깁니다.
 *  모바일 사용자는 글을 열어야 조작할 수 있는데, 그 컨트롤마저 숨은 드롭다운
 *  안에 있어 사실상 공유·삭제를 쓸 수 없었습니다.
 *
 *  숨은 메뉴에 기대지 않고, 필요한 버튼을 글 하단에 '드러냅니다'.
 *
 *    나만의 비밀방  → [함께 읽는 방에 나누기] [삭제]   ('답장' 제거)
 *    함께 읽는 방    → 코어 기본(답장·좋아요·신고) 유지
 *
 *  2026-08-03 · 자가 탈퇴 추가 (설정 화면 하단)
 *
 *  2026-08-05 · 마무리 작업 A 묶음
 *    A-1  계정 드롭다운·설정 화면에 약관·개인정보·가이드 링크 상시 노출
 *    A-2  카카오 가입자에게 이메일·비밀번호 변경 메뉴 숨김
 *    A-3  사용자 페이지 우상단 '⋯' 메뉴 숨김 (탈퇴 경로를 하나로)
 *    B-2  글 삭제 후 뒤로가기 404 수정
 * ==========================================================================*/

function tagIdsOf(discussion) {
    const tags = (discussion && typeof discussion.tags === 'function') ? discussion.tags() : [];
    return (tags || []).map((t) => String(t.id()));
}

function isOwnPrivateDiscussion(discussion) {
    if (!app.session.user || !discussion) return false;
    const author = discussion.user();
    if (!author || String(author.id()) !== String(app.session.user.id())) return false;
    return tagIdsOf(discussion).indexOf(PRIVATE_TAG_ID) !== -1;
}

function hasSharedTag(discussion) {
    return tagIdsOf(discussion).indexOf(SHARED_TAG_ID) !== -1;
}

/* ---------------------------------------------------------------------------
 *  A-2 · 이 계정이 '소셜 로그인 전용'인가
 * ---------------------------------------------------------------------------
 *  카카오로 들어온 사람에게는 이메일도 비밀번호도 없습니다.
 *  이메일은 우리가 지어낸 합성 주소이고, 비밀번호는 임의 문자열입니다.
 *
 *  그런데도 설정 화면에 '이메일 변경'과 '비밀번호 변경'이 보이면 두 가지가
 *  잘못됩니다.
 *
 *    1) 개인정보처리방침과 어긋납니다. "이메일을 수집하지 않는다"고 써두고
 *       입력란을 열어두면, 사용자가 실제 주소를 넣는 순간 우리가 갖지 않겠다고
 *       한 데이터를 갖게 됩니다.
 *    2) 계정이 깨집니다. 합성 주소를 실제 주소로 바꾸면 인증 메일이 날아가고,
 *       비밀번호는 본인도 모르니 확인 절차를 통과할 수 없습니다.
 *
 *  ⚠ 화면에서 지우는 것은 화면일 뿐입니다. 변경 API 는 여전히 열려 있습니다.
 *     베타 규모에서는 감수하지만, 유료 전환 전에는 서버에서도 막아야 합니다.
 *
 *  이메일로 직접 가입한 기존 계정은 그대로 둡니다. 그분들에게는 비밀번호
 *  변경이 유일한 복구 수단이라, 숨기면 계정에 갇히게 됩니다.
 * ------------------------------------------------------------------------ */
function isSocialOnlyAccount(user) {
    if (!user || typeof user.email !== 'function') return false;

    const email = user.email();
    // 이메일을 읽을 수 없으면 '아니다'로 봅니다.
    // 잘못 숨겨서 사람을 가두는 쪽이, 안 숨기는 쪽보다 나쁩니다.
    if (typeof email !== 'string' || email === '') return false;

    return email.toLowerCase().endsWith(SYNTHETIC_EMAIL_DOMAIN);
}

/* ---------------------------------------------------------------------------
 *  공유: 비밀방 글의 내용을 복제해 '함께 읽는 방'에 새 글로 올리고,
 *        원본에는 '공유됨' 태그를 붙여 재공유를 막습니다.
 * ------------------------------------------------------------------------ */
function shareToLounge(discussion) {
    if (!confirm('한번 공유된 내용은 수정하거나 지울 수 없습니다. 동의하십니까?')) return;

    const apiUrl = app.forum.attribute('apiUrl');

    app.request({ method: 'GET', url: apiUrl + '/discussions/' + discussion.id() })
        .then((response) => {
            const included = response.included || [];
            const firstPostRel = response.data.relationships.posts.data[0];
            const firstPost = included.find(
                (item) => item.type === 'posts' && String(item.id) === String(firstPostRel.id)
            );
            const title = response.data.attributes.title;
            const content = firstPost ? firstPost.attributes.content : '';

            return app.request({
                method: 'POST',
                url: apiUrl + '/discussions',
                body: {
                    data: {
                        type: 'discussions',
                        attributes: { title, content },
                        relationships: {
                            tags: { data: [{ type: 'tags', id: LOUNGE_TAG_ID }] },
                        },
                    },
                },
            });
        })
        .then(() =>
            app.request({
                method: 'PATCH',
                url: apiUrl + '/discussions/' + discussion.id(),
                body: {
                    data: {
                        type: 'discussions',
                        id: String(discussion.id()),
                        relationships: {
                            tags: {
                                data: [
                                    { type: 'tags', id: PRIVATE_TAG_ID },
                                    { type: 'tags', id: SHARED_TAG_ID },
                                ],
                            },
                        },
                    },
                },
            })
        )
        .then(() => { window.location.reload(); })
        .catch((err) => {
            console.error('private room share failed:', err);
            alert('공유에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        });
}

/* ---------------------------------------------------------------------------
 *  삭제: 숨김 처리 후 완전 삭제.
 *        Flarum 은 두 단계(숨김 → 삭제)를 거쳐야 영구 삭제가 됩니다.
 * ---------------------------------------------------------------------------
 *  B-2 (2026-08-05) · 삭제 후 뒤로가기가 '페이지를 찾을 수 없음'이 되던 문제
 *
 *    location.href 는 방문 기록에 새 항목을 '쌓습니다'. 그래서 기록이
 *        [ ..., /d/106(지워진 글), /t/private-room ]
 *    이 되고, 뒤로가기가 지워진 글로 돌아가 404 를 만났습니다.
 *
 *    location.replace 는 현재 항목을 '덮어씁니다'. 지워진 글의 주소가 기록에서
 *    사라지므로 뒤로가기는 그 이전 화면으로 갑니다.
 * ------------------------------------------------------------------------ */
function deleteDiscussion(discussion) {
    if (!confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다')) return;

    const alreadyHidden = typeof discussion.isHidden === 'function'
        ? discussion.isHidden()
        : !!discussion.attribute('isHidden');

    const hideStep = alreadyHidden
        ? Promise.resolve()
        : discussion.save({ isHidden: true });

    hideStep
        .then(() =>
            app.request({
                method: 'DELETE',
                url: app.forum.attribute('apiUrl') + '/discussions/' + discussion.id(),
            })
        )
        .then(() => { window.location.replace('/t/private-room'); })
        .catch((err) => {
            console.error('private room delete failed:', err);
            alert('삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        });
}

/* ---------------------------------------------------------------------------
 *  자가 탈퇴
 * ---------------------------------------------------------------------------
 *  서버 쪽 UserPolicy 가 '자기 자신'에 한해 삭제를 허용합니다.
 *  삭제되면 DeleteUserContent 리스너가 그 사람의 글까지 정리합니다.
 *
 *  추가 인증(비밀번호·이메일)은 요구하지 않습니다. 이미 로그인한 상태이고,
 *  그 로그인은 카카오가 보증한 것입니다. 대신 되돌릴 수 없는 행동이므로
 *  무엇이 사라지는지 명확히 알린 뒤 한 번 확인합니다.
 * ------------------------------------------------------------------------ */
function eraseOwnAccount() {
    const user = app.session.user;
    if (!user) return;

    const ok = confirm(
        '정말 탈퇴하시겠습니까?\n\n' +
        '· 나만의 비밀방에 남긴 기록이 모두 삭제됩니다\n' +
        '· 함께 읽는 방에 나눈 글도 함께 삭제됩니다\n' +
        '· 삭제된 내용은 복구할 수 없습니다\n\n' +
        '계속하시려면 확인을 눌러주세요.'
    );
    if (!ok) return;

    app.request({
        method: 'DELETE',
        url: app.forum.attribute('apiUrl') + '/users/' + user.id(),
    })
        .then(() => {
            // 계정이 사라지면 세션도 무효해집니다. 첫 화면으로 보냅니다.
            //
            // 2026-08-11 · B-2 와 같은 이유로 replace 를 씁니다.
            //  탈퇴하면 DeleteUserContent 가 그 사람의 글까지 지웁니다. href 로
            //  옮기면 방금 사라진 글들의 주소가 방문 기록에 그대로 남아, 뒤로가기
            //  한 번에 404 를 만납니다. 마지막 화면이 오류라면 곱지 않은 배웅입니다.
            window.location.replace('/');
        })
        .catch((err) => {
            console.error('account erase failed:', err);
            alert('탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        });
}

app.initializers.add('leo-private-room-permissions', () => {

    /* ====================================================================
     *  A-1 · 약관 · 개인정보처리방침 · 커뮤니티 가이드 상시 노출
     * --------------------------------------------------------------------
     *  가입 화면에서 한 번 스쳐 지나가는 것으로는 부족합니다.
     *  개인정보보호법 제30조는 처리방침을 '공개'하고 열람할 수 있게 두라고
     *  요구하고, 약관도 같습니다. 한 번 보여주고 감추면 요건을 못 채웁니다.
     *
     *  두 곳에 넣습니다.
     *    · 계정 드롭다운 — 모바일에서 손가락이 가장 짧게 닿는 곳
     *    · 설정 화면 하단 — 드롭다운을 못 찾은 사람을 위한 두 번째 길
     * ==================================================================== */
    extend(SessionDropdown.prototype, 'items', function (items) {
        items.add('onuDocsSeparator', Separator.component(), -85);

        items.add(
            'onuTerms',
            LinkButton.component({ href: '/p/terms', icon: 'fas fa-file-lines' }, '이용약관'),
            -86
        );
        items.add(
            'onuPrivacy',
            LinkButton.component({ href: '/p/privacy', icon: 'fas fa-user-shield' }, '개인정보처리방침'),
            -87
        );
        items.add(
            'onuGuide',
            LinkButton.component({ href: '/p/guide', icon: 'fas fa-hand-holding-heart' }, '커뮤니티 가이드'),
            -88
        );
    });

    /* ====================================================================
     *  A-2 · 이메일 · 비밀번호 변경 숨김 (카카오 가입자만)
     * --------------------------------------------------------------------
     *  ⚠ 'account' 묶음을 통째로 지우지 않습니다.
     *     그 안에는 fof/nicknames 가 넣는 '별명 설정하기'도 함께 들어 있고,
     *     첫 편지에서 "설정에서 별명을 바꾸실 수 있어요"라고 안내하고 있어
     *     묶음을 지우면 안내와 화면이 어긋납니다.
     *     그래서 두 항목만 골라 뺍니다.
     * ==================================================================== */
    extend(SettingsPage.prototype, 'accountItems', function (items) {
        if (!isSocialOnlyAccount(app.session.user)) return;

        items.remove('changeEmail');
        items.remove('changePassword');
    });

    /* ====================================================================
     *  A-3 · 사용자 페이지 우상단 '⋯' 메뉴 숨김
     * --------------------------------------------------------------------
     *  그 메뉴에는 Flarum 기본 '삭제' 하나만 들어 있었습니다.
     *  화면 맨 아래 '탈퇴하기'와 기능이 겹쳐 사용자가 어느 쪽인지 헷갈립니다.
     *
     *  게다가 기본 메뉴는 "사용자는 삭제되지만 글은 남습니다"라는 코어 경고를
     *  띄웁니다. 우리는 DeleteUserContent 리스너로 글까지 지우고 있어서
     *  그 문구가 사실과 다릅니다. 잘못된 안내를 고치는 가장 확실한 방법은
     *  잘못된 안내를 없애는 것입니다.
     *
     *  Flarum 은 컨트롤이 하나도 없으면 '⋯' 버튼 자체를 그리지 않습니다.
     *  그래서 항목만 빼면 메뉴가 사라집니다.
     *
     *  관리자에게는 남깁니다. 다른 사용자를 관리할 창구가 필요합니다.
     * ==================================================================== */
    extend(UserControls, 'destructiveControls', function (items) {
        const actor = app.session.user;
        if (!actor) return;
        if (typeof actor.isAdmin === 'function' && actor.isAdmin()) return;

        items.remove('delete');
    });

    /* ====================================================================
     *  비밀방 글에 표시용 클래스를 붙입니다.
     * --------------------------------------------------------------------
     *  글 하단의 '답장' 버튼은 코어가 아니라 flarum/mentions 확장이 넣습니다.
     *  확장 로드 순서는 보장되지 않아, actionItems 에서 remove('reply') 를
     *  해도 mentions 가 나중에 다시 붙이면 되살아납니다.
     *
     *  그래서 순서에 기대지 않고, 이 클래스를 기준으로 CSS 에서 감춥니다.
     *      .onu-private-post .Post-actions .item-reply { display: none }
     * ==================================================================== */
    extend(CommentPost.prototype, 'elementAttrs', function (attrs) {
        const post = this.attrs && this.attrs.post;
        if (!post) return;

        const discussion = typeof post.discussion === 'function' ? post.discussion() : null;
        if (!isOwnPrivateDiscussion(discussion)) return;

        attrs.className = (attrs.className || '') + ' onu-private-post';
    });

    /* ====================================================================
     *  글 하단에 버튼을 드러냅니다 — 모바일 대응
     *  첫 글에만 붙입니다. 공유·삭제는 '글 전체'에 대한 행동이므로
     *  댓글마다 반복될 이유가 없습니다.
     * ==================================================================== */
    extend(CommentPost.prototype, 'actionItems', function (items) {
        const post = this.attrs && this.attrs.post;
        if (!post) return;

        const discussion = typeof post.discussion === 'function' ? post.discussion() : null;
        if (!isOwnPrivateDiscussion(discussion)) return;

        // 비밀방은 혼자 쓰는 공간이라 '답장'이 의미 없습니다.
        // 우리 확장이 mentions 보다 나중에 로드되면 이것으로 지워지고,
        // 먼저 로드되면 위 elementAttrs 가 붙인 클래스 + CSS 가 처리합니다.
        items.remove('reply');

        // 첫 글에만 (number()가 1)
        const num = typeof post.number === 'function' ? post.number() : null;
        if (num !== 1) return;

        if (!hasSharedTag(discussion)) {
            items.add(
                'onuShare',
                Button.component(
                    {
                        className: 'Button Button--link onu-post-action',
                        icon: 'fas fa-share',
                        onclick: () => shareToLounge(discussion),
                    },
                    '함께 읽는 방에 나누기'
                ),
                20
            );
        }

        items.add(
            'onuDelete',
            Button.component(
                {
                    className: 'Button Button--link onu-post-action onu-post-action--danger',
                    icon: 'fas fa-trash',
                    onclick: () => deleteDiscussion(discussion),
                },
                '삭제'
            ),
            10
        );
    });

    /* ====================================================================
     *  설정 화면 하단 — 문서 링크 + 탈퇴
     *  이용약관 제9조 "언제든지 계정 설정에서 탈퇴할 수 있습니다" 이행.
     * ==================================================================== */
    extend(SettingsPage.prototype, 'settingsItems', function (items) {
        const user = app.session.user;
        if (!user) return;

        // A-1 두 번째 길 — 드롭다운을 못 찾은 사람을 위해
        items.add(
            'onuDocs',
            FieldSet.component(
                { className: 'Settings-account onu-docs', label: '약관 및 정책' },
                [
                    m('ul', { className: 'onu-docs-list' }, [
                        m('li', m('a', { href: '/p/terms' }, '이용약관')),
                        m('li', m('a', { href: '/p/privacy' }, '개인정보처리방침')),
                        m('li', m('a', { href: '/p/guide' }, '커뮤니티 가이드')),
                    ]),
                ]
            ),
            -40
        );

        // 관리자는 이 경로로 자신을 지울 수 없습니다 (서버 정책과 동일).
        if (typeof user.isAdmin === 'function' && user.isAdmin()) return;

        items.add(
            'onuAccountErasure',
            FieldSet.component(
                { className: 'Settings-account onu-erasure', label: '계정' },
                [
                    m('p', { className: 'onu-erasure-desc' },
                        '탈퇴하시면 나만의 비밀방의 기록과 함께 읽는 방에 나눈 글이 모두 삭제됩니다. 복구할 수 없습니다.'
                    ),
                    Button.component(
                        {
                            className: 'Button onu-erasure-btn',
                            onclick: eraseOwnAccount,
                        },
                        '탈퇴하기'
                    ),
                ]
            ),
            -50   // 설정 목록 맨 아래
        );
    });

    /* ====================================================================
     *  글 목록의 '⋮' 메뉴 — 데스크톱 편의용으로 유지
     *  (모바일에서는 Flarum 이 이 메뉴를 숨기므로 위 버튼이 주 경로입니다)
     * ==================================================================== */
    extend(DiscussionControls, 'moderationControls', function (items, discussion) {
        if (!isOwnPrivateDiscussion(discussion) || hasSharedTag(discussion)) return;

        items.add(
            'privateRoomShare',
            Button.component(
                { icon: 'fas fa-share', onclick: () => shareToLounge(discussion) },
                '공유하기'
            )
        );
    });

    extend(DiscussionControls, 'destructiveControls', function (items, discussion) {
        if (!isOwnPrivateDiscussion(discussion)) return;

        items.remove('hide');
        items.remove('restore');
        items.remove('delete');

        items.add(
            'privateRoomPermanentDelete',
            Button.component(
                { icon: 'fas fa-trash', onclick: () => deleteDiscussion(discussion) },
                '삭제'
            )
        );
    });
});

/* ============================================================================
 *  좋아요 문구 한글화  (2026-08-11)
 * ----------------------------------------------------------------------------
 *  왜 언어팩만으로는 안 되는가
 *
 *  flarum-lang/korean 의 실제 값입니다.
 *      you_text     => core.ref.you         = '당신'
 *      others_link  => core.ref.some_others = '{count} 개의 기타들'
 *      liked_by_self_text = '{users}님이 이 게시물을 좋아합니다.'
 *      liked_by_text      = '{count, plural, ...}}.'      ← 마침표가 두 번
 *
 *  그래서 화면에 '당신님이 …' 가 나오고, 좋아요가 넷을 넘으면
 *  '나, 나그네 및 2 개의 기타들 님이 이 게시물을 좋아합니다..' 가 됩니다.
 *  others 를 '기타'로 옮긴 자리에서 사람이 물건이 되었습니다.
 *
 *  문자열만으로 고칠 수 없는 이유는 조사입니다. 한국어는 이름 뒤 주격 조사가
 *  받침에 따라 이/가로 갈립니다 — '나그네가', '창문이'. 별명이 무엇으로 끝날지
 *  알 수 없으니 하나의 템플릿으로는 늘 한쪽이 틀립니다. 인스타그램이
 *  '회원님 외 32명이'처럼 언제나 '명'으로 끝맺는 것도 이 문제를 피하려는
 *  관례입니다.
 *
 *  여기서는 받침을 직접 보고 조사를 골라, 이름을 살린 표기를 씁니다.
 *      내가 좋아해요
 *      창가에 앉은 나그네가 좋아해요
 *      나, 창가에 앉은 나그네 외 2명이 좋아해요
 *
 *  ⚠ 초기화 우선순위를 -100 으로 두어 flarum/likes 의 footerItems 확장보다
 *    반드시 뒤에 실행되게 합니다. 앞서면 우리가 고친 것을 likes 가 다시 덮습니다.
 *    (mentions 의 '답장' 버튼에서 겪은 순서 문제와 같은 함정입니다. 그때는
 *    CSS 로 피했지만, 여기서는 순서를 못 박습니다.)
 *
 *  ⚠ locale/ko.yml 에도 같은 취지의 안전망 문구를 두었습니다. 조사를 붙이지 않은
 *    형태라 이 코드가 어떤 이유로 못 돌더라도 문장이 틀리지는 않습니다.
 * ==========================================================================*/

/** 한글 받침을 보고 주격 조사(이/가)를 고릅니다. 한글 음절이 아니면 '가'. */
function subjectParticle(word) {
    const s = String(word || '').trim();
    if (!s) return '가';
    const code = s.charCodeAt(s.length - 1);
    if (code < 0xac00 || code > 0xd7a3) return '가';
    return (code - 0xac00) % 28 === 0 ? '가' : '이';
}

/* ============================================================================
 *  포스트별 '댓글' 버튼 — 멘션을 꽂지 않습니다  (2026-08-12)
 * ----------------------------------------------------------------------------
 *  증상
 *    글 아래 '댓글'을 누르면 입력창에 이렇게 꽂힙니다.
 *        @"가을 하늘아래 수국"#p202
 *    #p202 는 flarum/mentions 가 "누구의 몇 번 글에 대한 답인지" 표시하는 내부
 *    문법입니다. 보낸 뒤 화면에는 멘션 칩으로만 나오고 번호는 사라지므로 유출은
 *    아니지만, 처음 댓글을 쓰려는 사람이 저 문자열을 보면 "이게 뭐지, 지워야
 *    하나" 하고 멈춥니다.
 *
 *  왜 CSS 로 감추지 않는가
 *    그 '댓글' 링크가 곧 mentions 의 reply_link 이고, 글 아래에서 댓글을 시작하는
 *    유일한 입구입니다. 감추면 한 줄로 끝나 보이지만 기능을 잃습니다.
 *    그래서 링크는 남기고 동작만 바꿉니다 — 입력창이 빈 상태로 열립니다.
 *
 *  ⚠ 우선순위 -100 초기화 안에 두는 이유는 좋아요 문구와 같습니다.
 *    mentions 가 'reply' 를 넣은 뒤에 우리가 갈아끼워야 합니다. 앞서면
 *    갈아끼울 대상이 아직 없습니다.
 * ==========================================================================*/

app.initializers.add(
    'leo-private-room-permissions-likes',
    () => {
        extend(CommentPost.prototype, 'actionItems', function (items) {
            if (!items || typeof items.has !== 'function' || !items.has('reply')) return;

            const post = this.attrs && this.attrs.post;
            if (!post) return;

            const discussion = typeof post.discussion === 'function' ? post.discussion() : null;
            if (!discussion) return;

            // 비밀방은 위쪽에서 이미 'reply' 를 지웁니다. 여기서는 함께 읽는 방만.
            if (isOwnPrivateDiscussion(discussion)) return;

            items.replace(
                'reply',
                Button.component(
                    {
                        className: 'Button Button--link',
                        icon: 'fas fa-reply',
                        onclick: () => {
                            // 입력창을 빈 상태로 엽니다. 멘션은 넣지 않습니다.
                            const p = DiscussionControls.replyAction.call(discussion, true, false);
                            if (p && typeof p.catch === 'function') p.catch(() => {});
                        },
                    },
                    '댓글'
                )
            );
        });

        extend(CommentPost.prototype, 'footerItems', function (items) {
            // likes 확장이 아직 항목을 넣지 않았다면 손댈 것이 없습니다.
            if (!items || typeof items.has !== 'function' || !items.has('liked')) return;

            const post = this.attrs && this.attrs.post;
            if (!post || typeof post.likes !== 'function') return;

            const likes = post.likes() || [];
            if (!likes.length) return;

            const me = app.session.user;
            const total = typeof post.likesCount === 'function'
                ? post.likesCount()
                : likes.length;

            // likes 확장과 같은 규칙: 최대 4명까지 이름, 넘치면 마지막에 '외 N명'
            const LIMIT = 4;
            const over = total > LIMIT;

            const sorted = likes.slice().sort((a) => (a === me ? -1 : 1));
            const shown = sorted.slice(0, over ? LIMIT - 1 : LIMIT);

            const names = shown.map((u) => {
                if (u === me) return '나';
                if (typeof u.displayName === 'function') return u.displayName();
                return typeof u.username === 'function' ? u.username() : '';
            }).filter(Boolean);

            if (!names.length) return;
            if (over) names.push('외 ' + (total - names.length) + '명');

            let subject;
            if (names.length === 1 && names[0] === '나') {
                // '나이 좋아해요' 를 피합니다. 혼자일 때는 '내가'.
                subject = '내가';
            } else {
                subject = names.join(', ') + subjectParticle(names[names.length - 1]);
            }

            items.replace(
                'liked',
                m('div', { className: 'Post-likedBy' }, [
                    m('i', { className: 'far fa-thumbs-up icon' }),
                    ' ' + subject + ' 좋아해요',
                ])
            );
        });
    },
    -100
);
